import {FilterIcon, SearchIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, asSlot, IconButton} from '@primer/react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import {useFilterableAgents, withBuiltinIdeAgentFallbacks} from '../../hooks/use-filterable-agents'
import {buildAgentOptions} from '../../hooks/use-filter-options'
import {
  getConsolidatedStatusOptionsForTab,
  getStatusOptionsForTab,
  getTypeOptionsForAgents,
  GROUP_BY_REPOSITORY,
  GROUP_BY_STATUS,
  GROUP_BY_LAST_UPDATED,
  GROUP_BY_ENVIRONMENT,
  type SidebarGroupBy,
} from '../../utils/filter-utils'
import {MultiSelectFilter} from '../filters/MultiSelectFilter'
import {RepoFilterPicker} from './RepoFilterPicker'
import styles from './AgentsSidebar.module.css'

interface SidebarFilterMenuProps {
  showSidebarFiltering: boolean
  showSidebarFacetFiltering: boolean
  showSidebarTypeFacet: boolean
  showSearch: boolean
  searchHref: string
  onSearchClick: (event: React.MouseEvent<HTMLAnchorElement>) => void
  filterPopoverOpen: boolean
  onFilterPopoverOpenChange: (open: boolean) => void
  hasActiveSidebarFilter: boolean
  isSidebarExpanded: boolean
  close: () => void
  onPlusButtonClick?: () => void
  navigateToHome: (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void
  groupBy: SidebarGroupBy | undefined
  onGroupByChange: (groupBy: SidebarGroupBy | undefined) => void
  selectedStatuses: string[]
  onStatusChange: (statuses: string[]) => void
  selectedTypes: string[]
  onTypeChange: (values: string[]) => void
  selectedAgents: string[]
  onAgentChange: (values: string[]) => void
  filterableAgents?: AgentInfo[]
  repoFilter?: string
  onRepoFilterChange: (repo: AgentDropdownRepository | undefined) => void
  onResetFilters: () => void
  filterButtonRef: React.RefObject<HTMLElement | null>
}

// Wraps the heading actions in a single slot so they render
// as siblings of the "Sessions" heading text rather than nested inside the <h2> element.
// See ActionList.GroupHeading's `trailingAction` slot, which only recognizes one child.
export const SidebarFilterMenu = asSlot(function SidebarFilterMenuComponent({
  showSidebarFiltering,
  showSidebarFacetFiltering,
  showSidebarTypeFacet,
  showSearch,
  searchHref,
  onSearchClick,
  filterPopoverOpen,
  onFilterPopoverOpenChange,
  hasActiveSidebarFilter: hasActiveFilter,
  isSidebarExpanded,
  close,
  onPlusButtonClick,
  navigateToHome,
  groupBy,
  onGroupByChange,
  selectedStatuses,
  onStatusChange,
  selectedTypes,
  onTypeChange,
  selectedAgents,
  onAgentChange,
  filterableAgents,
  repoFilter,
  onRepoFilterChange,
  onResetFilters,
  filterButtonRef,
}: SidebarFilterMenuProps) {
  const statusOptions = isFeatureEnabled('copilot_mission_control_filters_followups')
    ? getConsolidatedStatusOptionsForTab('open', [])
    : getStatusOptionsForTab('open')
  const filterableAgentsQuery = useFilterableAgents({
    enabled: showSidebarFacetFiltering && filterPopoverOpen && !filterableAgents,
    preserveErrors: true,
  })
  const availableAgents = filterableAgentsQuery.isError
    ? []
    : (filterableAgents ??
      (filterableAgentsQuery.isSuccess ? withBuiltinIdeAgentFallbacks(filterableAgentsQuery.data) : []))
  const typeOptions = getTypeOptionsForAgents(availableAgents)
  const agentOptions = buildAgentOptions(availableAgents)
  return (
    <span className={styles.sessionsSectionActions}>
      {showSidebarFiltering && showSearch && (
        <ActionList.GroupHeading.TrailingAction
          as="a"
          href={searchHref}
          icon={SearchIcon}
          label="Search sessions"
          tooltipDirection="s"
          onClick={onSearchClick}
        />
      )}
      {showSidebarFiltering && (
        <ActionMenu anchorRef={filterButtonRef} open={filterPopoverOpen} onOpenChange={onFilterPopoverOpenChange}>
          <ActionMenu.Anchor>
            <IconButton
              type="button"
              icon={FilterIcon}
              variant="invisible"
              size="small"
              tooltipDirection="s"
              aria-label={hasActiveFilter ? 'Filter views (filters applied)' : 'Filter views'}
              className={hasActiveFilter ? styles.filterActive : undefined}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay align="end" side="outside-bottom">
            <ActionList>
              <ActionList.Group selectionVariant="single">
                <ActionList.GroupHeading>Group by</ActionList.GroupHeading>
                <ActionList.Item
                  selected={groupBy === GROUP_BY_LAST_UPDATED || !groupBy}
                  onSelect={() => onGroupByChange(GROUP_BY_LAST_UPDATED)}
                >
                  Last updated
                </ActionList.Item>
                <ActionList.Item
                  selected={groupBy === GROUP_BY_REPOSITORY}
                  onSelect={() => onGroupByChange(GROUP_BY_REPOSITORY)}
                >
                  Repository
                </ActionList.Item>
                <ActionList.Item
                  selected={groupBy === GROUP_BY_STATUS}
                  onSelect={() => onGroupByChange(GROUP_BY_STATUS)}
                >
                  Status
                </ActionList.Item>
                <ActionList.Item
                  selected={groupBy === GROUP_BY_ENVIRONMENT}
                  onSelect={() => onGroupByChange(GROUP_BY_ENVIRONMENT)}
                >
                  Environment
                </ActionList.Item>
              </ActionList.Group>
              <ActionList.Divider />
              <ActionList.Group>
                <ActionList.GroupHeading>Filter by</ActionList.GroupHeading>
                <MultiSelectFilter
                  label="Status"
                  selected={selectedStatuses}
                  options={statusOptions}
                  onSelectionChange={onStatusChange}
                  nested
                  sidebarSubmenu
                />
                <RepoFilterPicker
                  repoFilter={repoFilter}
                  onFilterChange={onRepoFilterChange}
                  nested
                  sidebarSubmenu
                  includeRepositoryPresenceOptions={false}
                />
                {showSidebarFacetFiltering && (
                  <>
                    {showSidebarTypeFacet && typeOptions.length > 0 && (
                      <MultiSelectFilter
                        label="Type"
                        selected={selectedTypes}
                        options={typeOptions}
                        onSelectionChange={onTypeChange}
                        nested
                        sidebarSubmenu
                      />
                    )}
                    {agentOptions.length > 1 && (
                      <MultiSelectFilter
                        label="Agent"
                        selected={selectedAgents}
                        options={agentOptions}
                        onSelectionChange={onAgentChange}
                        nested
                        sidebarSubmenu
                      />
                    )}
                  </>
                )}
              </ActionList.Group>
              {hasActiveFilter && (
                <>
                  <ActionList.Divider />
                  <ActionList.Item onSelect={onResetFilters}>Reset filters</ActionList.Item>
                </>
              )}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      <ActionList.GroupHeading.TrailingAction
        as="button"
        className={isSidebarExpanded ? styles.expandedSessionsTrailingAction : undefined}
        onClick={event => {
          close()
          navigateToHome(event)
          onPlusButtonClick?.()
        }}
        icon={PlusIcon}
        label="Start a new session"
        tooltipDirection="s"
      />
    </span>
  )
}, ActionList.GroupHeading.TrailingAction)
